import React, { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import cs from 'classnames';
import { Button, Cascader, Col, Form, Row, Space } from 'antd';
import styles from './index.module.less';
import V2Drawer from '@/common/components/Feedback/V2Drawer';
import V2Title from '@/common/components/Feedback/V2Title';
import V2Form from '@/common/components/Form/V2Form';
import V2FormProvinceList from '@/common/components/Form/V2FormProvinceList';
import V2FormDatePicker from '@/common/components/Form/V2FormDatePicker/V2FormDatePicker';
import V2FormInput from '@/common/components/Form/V2FormInput/V2FormInput';
import V2FormSelect from '@/common/components/Form/V2FormSelect/V2FormSelect';
import CircleBusinessMap from 'src/common/components/business/ExpandStore/CircleBusinessMap/index';
import V2FormRadio from '@/common/components/Form/V2FormRadio/V2FormRadio';
import { isArray, isNotEmptyAny, refactorSelection } from '@lhb/func';
import { employeeListPermission } from '@/common/api/employee';
import { getCookie } from '@lhb/cache';
import V2Message from '@/common/components/Others/V2Hint/V2Message';
import dayjs from 'dayjs';
import { createCircleTask, getStandardTaskSelection } from '@/common/api/expandStore/expansiontask';
import { useBigdataFn } from '@/common/utils/bigdata';
import { useSelector } from 'react-redux';
import { AssignType } from '@/common/components/business/ExpandStore/ts-config';
import UserInfoContext from '@/layout/context';
import { globalExpansionTaskAlias, globalOpeningAreaAlias } from '@/common/utils/ways';
import { useMethods } from '@lhb/hook';
import V2Container from '@/common/components/Data/V2Container';

interface CircleTaskCreateDrawerProps {
  /** 是否显示抽屉 */
  showDrawer: boolean;
  /** 控制是否显示抽屉 */
  setShowDrawer: React.Dispatch<boolean>;
  /** 指定加盟商信息id，可选；指定时不可更改加盟商 */
  // franchiseeId?: number;
  /** 提交表单后的回调 */
  refresh?: ()=>void;
  businessInfo?:any;
  isBusiness?:boolean;
}

/**
 * 拓店任务-新增/编辑抽屉
 * @Description 拓店任务：直营
 */
const CircleTaskCreateDrawer: FC<CircleTaskCreateDrawerProps> = ({
  showDrawer,
  setShowDrawer,
  isBusiness = false,
  businessInfo,
  refresh
}) => {

  const [innerMainHeight, setInnerMainHeight] = useState<number>(0);

  const [form] = Form.useForm(); // 指派
  const bigdataFn = useBigdataFn(); // 使用埋点 Hook
  const circlebusinessMap: any = useRef();
  const [requesting, setRequesting] = useState(false);
  const [employeeOptions, setEmployeeOptions] = useState<any>([]); // 指派人选项
  const [emergencyOptions, setEmergencyOptions] = useState<any>([]);
  const [kwkEmergencyOptions, setKwkEmergencyOptions] = useState<any>({
    emergencyDegreeByChancePoint: [],
    emergencyDegreeBySpotSurvey: []
  });
  const targetAddressInfo = Form.useWatch('targetAddressInfo', form);
  const businessName = Form.useWatch('businessName', form);
  const tenantCheckData = useSelector((state: any) => state.common.tenantCheck);
  const isAssignTypeLeader = [AssignType.Twice, AssignType.ThreeTimes].includes(tenantCheckData?.taskAssignType); // 是否指派主管（二次、三次指派兼容为同样处理）
  const { tenantInfo: { globalAliasItemVOs } } = useContext(UserInfoContext); // 租户信息
  const expansionTaskAlias = globalExpansionTaskAlias(globalAliasItemVOs);

  // 是否卡旺卡租户,0626改为是否拓店任务模版
  const isKwk = useMemo(() => {
    return (tenantCheckData?.taskTemplateCode) === 'clusterB';
  }, [tenantCheckData]);

  const openingAreaTaskAlias = useMemo(() => {
    return globalOpeningAreaAlias(globalAliasItemVOs);
  }, [globalAliasItemVOs]);

  useEffect(() => {
    methods.getEmployeeOptions();
    if (isBusiness) {
      const { id, areaName, provinceId, cityId, districtId, provinceName, cityName, districtName } = businessInfo;
      form.setFieldsValue({
        businessName: areaName,
        targetAddress: [provinceId, cityId, districtId],
        targetAddressInfo: [provinceName, cityName, districtName],
        circleBusinessId: id
      });

    }
  }, []);

  useEffect(() => {
    // const targetCity = targetAddressInfo && isArray(targetAddressInfo) ? `${targetAddressInfo.slice(0, 2).join('/')}_` : '';
    const targetCity = targetAddressInfo && isNotEmptyAny(targetAddressInfo[1]) ? `${targetAddressInfo[1]}_` : '';
    const targetBusinessName = businessName ? `${businessName}_` : '';

    const taskName = (targetCity || targetBusinessName) ? `${targetCity}${targetBusinessName}${expansionTaskAlias || '拓店任务'}` : '';
    form.setFieldsValue({
      name: taskName
    });
  }, [targetAddressInfo, businessName]);

  const methods = useMethods({

    /**
     * @description 获取可指派的团队人员
     */
    getEmployeeOptions: async () => {
      const params = isAssignTypeLeader && isArray(tenantCheckData?.taskAssignRoleIds) ? { roleIds: tenantCheckData?.taskAssignRoleIds } : {};
      const { objectList: data } = await employeeListPermission(params);

      const options = await getStandardTaskSelection();
      const arr = isArray(data) && data.length ? data.map((item) => ({
        ...item,
        name: `${item.name} ${item.mobile}`
      })) : [];
      const emergencyData = options?.emergencyDegree.map((item) => ({
        label: item.name,
        value: item.id
      })) || [];
      setEmergencyOptions(emergencyData);
      const emergencyDegreeByChancePoint = refactorSelection(options?.emergencyDegreeByChancePoint);
      const emergencyDegreeBySpotSurvey = refactorSelection(options?.emergencyDegreeBySpotSurvey);
      setKwkEmergencyOptions({
        emergencyDegreeByChancePoint,
        emergencyDegreeBySpotSurvey
      });

      setEmployeeOptions(arr);
      if (isAssignTypeLeader && arr.length === 1) { // 指派主管且只有一个时默认指定
        form.setFieldValue('managerId', arr[0].id);
      }
    },

    /**
     * 创建拓店任务
     * @returns 创建拓店任务
     */
    onSubmit: async():Promise<void> => {
      const { radio, assignAccountIds, targetAddress, ...values } = await form.validateFields();
      const { managerId } = values;
      bigdataFn('ba1786de-ac00-484e-8566-ababe085a3c3', '创建拓店任务-点击确定', '点击了创建拓店任务-点击确定', { op_block: '创建拓店任务', op_event: ``, });
      let assignAccount: any[] = [];
      if (radio === 1) {
        const accountId = getCookie('employeeId');
        assignAccount = [accountId];
      } else if (radio === 2) {
        if (isAssignTypeLeader) {
          if (!managerId) {
            V2Message.warning('请选择开发主管');
            return;
          }
        } else if (assignAccountIds) {
          assignAccount = [assignAccountIds];
        } else {
          V2Message.warning('请选择指派的团队成员');
          return;
        }
      } else {
        V2Message.warning('请选择指派的成员');
        return;
      }
      if (targetAddress && !targetAddress[1]) {
        V2Message.warning('城区请选择到市');
        return;
      }
      try {
        setRequesting(true);
        const {
          name,
          emergencyDegree,
          expectDropInDate,
        } = values;
        let emergencyTmp = emergencyDegree;
        if (isArray(emergencyTmp)) { // 多级紧急度数据
          emergencyTmp = emergencyTmp.length ? emergencyTmp[emergencyTmp.length - 1] : null;
        }

        const circleBusinessId = form.getFieldValue('circleBusinessId');
        // 构建目标城市参数
        const targetAddNew = targetAddress && {
          targetProvinceId: targetAddress[0] || undefined,
          targetCityId: targetAddress[1] || undefined,
          targetDistrictId: targetAddress[2] || undefined,
        };
        const params: any = {
          name,
          expectDropInDate: expectDropInDate && dayjs(expectDropInDate).format('YYYY-MM-DD'),
          emergencyDegree: emergencyTmp,
          spotSurveyEmergencyDegree: isKwk ? values?.spotSurveyEmergencyDegree : null,
          chancePointEmergencyDegree: isKwk ? values?.chancePointEmergencyDegree : null,
          ...targetAddNew,
          modelClusterId: circleBusinessId,
        };
        // 指派自己、或者指派其他成员时传assignAccountIds，指派主管用managerId
        Object.assign(params, radio === 2 && isAssignTypeLeader ? { managerId } : {
          assignAccountIds: assignAccount,
        });
        createCircleTask(params).then(() => {
          V2Message.success(`创建${expansionTaskAlias || '拓店任务'}成功`);
          setShowDrawer(false);
          refresh && refresh();
        })
          .catch((err) => {
            setShowDrawer(false);
            console.log('catch 失败', err);
            V2Message.error(`创建${expansionTaskAlias || '拓店任务'}失败`);
          }).finally(() => {
            setRequesting(false);
          });
      } catch (error) {
        console.log('error', error);
        setShowDrawer(false);
        setRequesting(false);
      }
    },

    /**
     * 取消创建拓店任务
     */
    onCancel: () => {
      setShowDrawer(false);
      setTimeout(() => {
        setRequesting(false);
      }, 100);
    }

  });

  return (
    <V2Drawer
      className={styles.createTaskDrawer}
      open={showDrawer}
      bodyStyle={{ padding: 0, }}
      onClose={() => setShowDrawer(false)}
      destroyOnClose
      // contentWrapperStyle={{
      //   minWidth: 'auto',
      //   width: '1008px',
      // }}
    >
      <V2Container
        emitMainHeight={(h) => setInnerMainHeight(h)}
        extraContent={{
          top: (<V2Title type='H1' text={`创建${expansionTaskAlias || '拓店任务'}`} className={styles.title} />),
          bottom: (<Space className={styles.bottom} size={12}>
            <Button type='default' onClick={methods.onCancel}>取消</Button>
            <Button type='primary' loading={requesting} onClick={methods.onSubmit}>确定</Button>
          </Space>)
        }}
        style={{ height: '100%', width: '100%' }}
        className={styles.container}>
        <V2Form form={form} className={cs('pt-16 pb-16 pl-40 pr-40', styles.editMain)} style={{ height: innerMainHeight }} >
          <V2Title type='H2' divider text={`${expansionTaskAlias || '拓店任务'}信息`} className={'mb-16'} />
          <Row gutter={24}>
            <Col span={24} className='mb-16'>
              <CircleBusinessMap form ={form} ref={circlebusinessMap} businessInfo={businessInfo} isBusiness={isBusiness} />
            </Col>
            <Col span={12}>
              <V2FormInput required label={openingAreaTaskAlias} name='businessName' disabled={true} placeholder={`请在地图上选择${openingAreaTaskAlias}`}/>
            </Col>
            <Col span={12}>
              <Form.Item name='targetAddressInfo' hidden noStyle/>
              <V2FormProvinceList
                required
                label='目标城市'
                name='targetAddress'
                disabled={true}
                config={{
                  value: [1],
                  changeOnSelect: true,
                  allowClear: true,
                  expandTrigger: 'hover',
                  showCheckedStrategy: Cascader.SHOW_PARENT,
                  onChange: (_, selectedOptions) => {
                    circlebusinessMap.current?.onChangeProvince && circlebusinessMap.current.onChangeProvince(_, selectedOptions);
                  },
                  onDropdownVisibleChange: (value) => {
                    circlebusinessMap.current?.onChangeProvince && circlebusinessMap.current.onDropdownVisibleChange(value);

                  }
                }}
              />
            </Col>
            <Col span={12}>
              <V2FormInput required label={`${expansionTaskAlias || '任务'}名称`} name='name' maxLength={50} />
            </Col>

            {
              isKwk ? <>
                <Col span={12}>
                  <V2FormSelect label='踩点紧急度' required name='spotSurveyEmergencyDegree' options={kwkEmergencyOptions.emergencyDegreeBySpotSurvey} />
                </Col>
                <Col span={12}>
                  <V2FormSelect label='备选点上传紧急度' required name='chancePointEmergencyDegree' options={kwkEmergencyOptions.emergencyDegreeByChancePoint} />
                </Col>
              </> : <Col span={12}>
                <V2FormSelect required label='紧急度' name='emergencyDegree' options={emergencyOptions} />
              </Col>
            }
            {!isKwk ? <Col span={12}>
              <V2FormDatePicker required label='期望落位日期' name='expectDropInDate' />
            </Col> : <></>}
          </Row>
          <V2Title type='H2' divider text={`选择${expansionTaskAlias || '拓店'}责任人`} style={{ margin: '16px 0' }} />
          <V2FormRadio
            name='radio'
            className={styles.radioList}
            options={[
              { label: '指派给自己', value: 1 },
              {
                label: isAssignTypeLeader ? (
                  <div className={styles.radioSelect}>
                        指派给开发主管
                    <V2FormSelect
                      className={styles.assignAccountIds}
                      placeholder='请选择开发主管'
                      name='managerId'
                      options={refactorSelection(employeeOptions)}
                      config={{
                        showSearch: true,
                        onClick: e => {
                          e.preventDefault();
                          e.stopPropagation;
                        }, // 解决下拉框闪现的情况
                        onFocus: () => {
                          form.setFieldValue('radio', 2);
                        }, // 下拉框获取焦点的时候单选选择第二个
                      }}
                    />
                  </div>
                ) : (
                  <div className={styles.radioSelect}>
                        指派给团队成员
                    <V2FormSelect
                      className={styles.assignAccountIds}
                      placeholder='请选择指派人'
                      name='assignAccountIds'
                      options={refactorSelection(employeeOptions)}
                      config={{
                        showSearch: true,
                        onClick: e => {
                          e.preventDefault();
                          e.stopPropagation;
                        }, // 解决下拉框闪现的情况
                        onFocus: () => {
                          form.setFieldValue('radio', 2);
                        }, // 下拉框获取焦点的时候单选选择第二个
                      }}
                    />
                  </div>
                ),
                value: 2,
              },
            ]}
          />
        </V2Form>
      </V2Container>
    </V2Drawer>
  );
};
export default CircleTaskCreateDrawer;
